import type {RosterSlot} from '../../../../lib/roster-colors';
import type {AdequacyStandard, ComparisonMeasure} from '../../lib/stores/roster-comparison-store';
import {ComparisonRow, hasRosterData, MEASURE_STANDARDS, meetsMeasure, populationMetKey} from '../../lib/comparison-row';

// A selected roster as the summary needs it: who it is, and where its figures sit in the response
export type SummaryRoster = {
    key: string;
    slot: RosterSlot;
    name: string;
    isPrimary: boolean;
    // The response numbers its per-roster fields (timeAdequacyFlag1, ...) by position in the request
    fieldNumber: number;
    // Whether the response has figures for the roster anywhere in the geography, whatever the
    // Specialty filter shows. One without is excluded from the summary
    hasData: boolean;
};

// average is the mean share of the population meeting the standard, as a fraction (0-1);
// null when the response has no usable figure for the roster, so it reads as no data, not 0%
export type RankedRoster = SummaryRoster & {average: number | null};

// Specialties a comparison roster meets that the primary misses (better), and the reverse (worse)
export type PrimaryContrast = SummaryRoster & {better: number; worse: number};

// A roster with data that lacks some of the specialties in scope, and how many
export type PartialRoster = SummaryRoster & {missing: number};

export type SplitSpecialty = {name: string; met: number};

// spread is the widest roster-to-roster difference in one specialty, as a fraction (0-1)
export type SpecialtyGap = {name: string; spread: number};

/**
 * Why the data leaves nothing to compare:
 * - no-data: no selected roster has data for the geography
 * - primary-no-data: the primary has none, so there is nothing to compare against
 * - too-few: only one roster has data
 * - no-common: no specialty in scope has data from every roster that has any
 */
export type SummaryBlocker = 'no-data' | 'primary-no-data' | 'too-few' | 'no-common';

export type ComparisonSummary = {
    // Most specific first; null when the comparison can be summarised
    blocker: SummaryBlocker | null;
    // Specialties in scope: the rows the table shows
    scopeCount: number;
    // Specialties summarised, fewer than scopeCount when some are left out
    specialtyCount: number;
    // The rosters with data, highest average first, rosters with no figures last; rosters with the
    // same average stay in slot order
    ranked: RankedRoster[];
    // Specialties where the rosters do not all agree on the verdict - some meet, some do not
    splits: SplitSpecialty[];
    largestGap: SpecialtyGap | null;
    againstPrimary: PrimaryContrast[];
    // Rosters with no data for the geography, left out of every figure
    excluded: SummaryRoster[];
    partial: PartialRoster[];
    // Specialties in scope left out of every roster's figures, as a roster with data lacks them
    leftOut: string[];
    // Every roster has the same figures in every specialty summarised
    identical: boolean;
};

// A comparison needs a roster to compare with the primary
const MIN_ROSTERS = 2;

// null when the response has no usable figure, so a gap is skipped rather than counted as 0%
const populationMetStandard = (row: ComparisonRow, standard: AdequacyStandard, roster: SummaryRoster) => {
    const raw = row[populationMetKey(standard, roster.fieldNumber)];
    // Checked before Number(), which turns null and '' into 0
    if (raw === null || raw === undefined || raw === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

// The lowest of the measure's shares. The response has no share meeting both standards, so Combined
// reads the binding one: no more of the population can meet both than meets either, and the
// table's Combined tick needs both. A mean would let a strong standard outrank a missed one.
// null unless every share is usable
const populationMet = (row: ComparisonRow, measure: ComparisonMeasure, roster: SummaryRoster) => {
    let lowest = Infinity;
    for (const standard of MEASURE_STANDARDS[measure]) {
        const share = populationMetStandard(row, standard, roster);
        if (share === null) return null;
        lowest = Math.min(lowest, share);
    }
    return lowest;
};

// Arithmetic on fractions leaves floating-point noise. 0.95 - 0.62 is 0.32999999999999996, which
// the flooring percent formatter shows as 32.9%, and 0.93 - 0.60 comes out above it, so it would
// win a tie. Rounded off far below the 0.1% displayed, equal figures are equal and read as such
const withoutFloatNoise = (n: number) => Math.round(n * 1e9) / 1e9;

const mean = (values: number[]) =>
    values.length ? withoutFloatNoise(values.reduce((sum, v) => sum + v, 0) / values.length) : null;

// Below every real share (0-1), so a roster with no figures ranks last
const NO_AVERAGE = -1;

// The figures the table shows for the measure: the verdict and each standard's share
const sameFigures = (row: ComparisonRow, measure: ComparisonMeasure, a: SummaryRoster, b: SummaryRoster) =>
    meetsMeasure(row, measure, a.fieldNumber) === meetsMeasure(row, measure, b.fieldNumber)
    && MEASURE_STANDARDS[measure].every((standard) =>
        populationMetStandard(row, standard, a) === populationMetStandard(row, standard, b));

const findBlocker = (
    included: SummaryRoster[],
    primary: SummaryRoster | undefined,
    summarisedCount: number,
    leftOutCount: number,
): SummaryBlocker | null => {
    if (!included.length) return 'no-data';
    if (primary && !primary.hasData) return 'primary-no-data';
    if (included.length < MIN_ROSTERS) return 'too-few';
    if (!summarisedCount && leftOutCount) return 'no-common';
    return null;
};

/**
 * Summarises a roster comparison response for one measure
 *
 * Everything is derived from the rows the table shows, never authored, so the panel cannot
 * disagree with the table beside it. Rows are the whole result rather than the current page:
 * a summary that changed when the page turned would describe the page, not the comparison.
 * The rows are the ones the table shows, so the Specialty filter narrows them to its selection.
 *
 * A roster with no data for the geography is excluded outright. A specialty any remaining roster
 * lacks is left out for every roster, so the comparison stays like for like: a roster missing its
 * weakest specialties would otherwise rank higher for having less data.
 *
 * @param rows - Comparison response rows, one per specialty
 * @param rosters - Selected rosters, each carrying its response field number
 * @param measure - The standard the verdicts and percentages are read against, or both for 'combined'
 */
export function summariseComparison(
    rows: ComparisonRow[],
    rosters: SummaryRoster[],
    measure: ComparisonMeasure,
): ComparisonSummary {
    const included = rosters.filter((r) => r.hasData);
    const excluded = rosters.filter((r) => !r.hasData);
    const complete = (row: ComparisonRow) => included.every((r) => hasRosterData(row, r.fieldNumber));
    const summarised = rows.filter(complete);
    const leftOut = rows.filter((row) => !complete(row)).map((row) => row.specialtyName);
    const partial = included
        .map((roster) => ({...roster, missing: rows.filter((row) => !hasRosterData(row, roster.fieldNumber)).length}))
        .filter((roster) => roster.missing > 0);

    const ranked = included
        .map((roster) => {
            const values = summarised
                .map((row) => populationMet(row, measure, roster))
                .filter((v): v is number => v !== null);
            return {...roster, average: mean(values)};
        })
        .sort((a, b) => ((b.average ?? NO_AVERAGE) - (a.average ?? NO_AVERAGE)) || (a.slot - b.slot));

    const splits: SplitSpecialty[] = [];
    let largestGap: SpecialtyGap | null = null;

    summarised.forEach((row) => {
        const verdicts = included.map((roster) => meetsMeasure(row, measure, roster.fieldNumber));
        const met = verdicts.filter(Boolean).length;
        if (met > 0 && met < verdicts.length) {
            splits.push({name: row.specialtyName, met});
        }

        const values = included
            .map((roster) => populationMet(row, measure, roster))
            .filter((v): v is number => v !== null);
        if (values.length > 1) {
            const spread = withoutFloatNoise(Math.max(...values) - Math.min(...values));
            // Strictly wider, so a tie keeps the specialty listed first
            if (!largestGap || spread > largestGap.spread) {
                largestGap = {name: row.specialtyName, spread};
            }
        }
    });

    // Against the primary only - four comparisons at five rosters rather than ten pairs
    const primary = rosters.find((r) => r.isPrimary);
    const againstPrimary = primary?.hasData
        ? included.filter((r) => !r.isPrimary).map((roster) => {
            let better = 0;
            let worse = 0;
            summarised.forEach((row) => {
                const primaryMeets = meetsMeasure(row, measure, primary.fieldNumber);
                const rosterMeets = meetsMeasure(row, measure, roster.fieldNumber);
                if (rosterMeets && !primaryMeets) better++;
                else if (primaryMeets && !rosterMeets) worse++;
            });
            return {...roster, better, worse};
        })
        : [];

    // The same displayed figures everywhere, not just the same verdicts: every roster agreeing on
    // every verdict is the case the splits fold already reports
    const identical = included.length >= MIN_ROSTERS
        && summarised.length > 0
        && summarised.every((row) => included.every((roster) => sameFigures(row, measure, included[0], roster)));

    return {
        blocker: findBlocker(included, primary, summarised.length, leftOut.length),
        scopeCount: rows.length,
        specialtyCount: summarised.length,
        ranked,
        splits,
        largestGap,
        againstPrimary,
        excluded,
        partial,
        leftOut,
        identical,
    };
}
